import React from "react";

import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@material-ui/core";
import clsx from "clsx";
import { RouteComponentProps } from "react-router";

import { ActionLink } from "halifax";

import {
  PATH_ANCILLARY_CUSTOMIZE,
  PATH_AVAILABILITY,
  PATH_BOOK,
  PATH_SHOP,
} from "../../../../utils/paths";
import { PremierCollectionShopStep } from "../../reducer";
import {
  goToAncillaryCustomize,
  goToAvailability,
  goToShop,
} from "../../utils/queryStringHelpers";
import * as constants from "./constants";
import { HotelShopProgressBarConnectorProps } from "./container";

import "./styles.scss";

export interface IHotelShopProgressBarProps
  extends HotelShopProgressBarConnectorProps,
    RouteComponentProps {
  useCustomizeStep?: boolean;
}

const AVAILABILITY_STEP = "availability-step";
const CUSTOMIZE_STEP = "customize-step";
const BOOK_STEP = "book-step";
type HotelShopStepV2 =
  | PremierCollectionShopStep
  | typeof AVAILABILITY_STEP
  | typeof CUSTOMIZE_STEP
  | typeof BOOK_STEP;

enum CompareStepsResult {
  Before,
  Same,
  After,
  Unknown,
}

export const HotelShopProgressBar = (props: IHotelShopProgressBarProps) => {
  const {
    hotelShopStep,
    chosenProduct,
    selectedLodging,
    fromDate,
    untilDate,
    adultsCount,
    childrenCount,
    isAddOnOptionAvailable,
    isOptionSelectionComplete,
    setPremierCollectionShopProgress,
    useCustomizeStep,
    history,
  } = props;
  const hasSelectedHotelProduct = !!chosenProduct && !!selectedLodging;

  const ProgressButtons = ({
    progresses,
  }: {
    progresses: HotelShopStepV2[];
  }) => {
    const selectedStep: HotelShopStepV2 = (() => {
      switch (history.location.pathname) {
        case PATH_AVAILABILITY:
          return AVAILABILITY_STEP;
        case PATH_BOOK:
          return BOOK_STEP;
        case PATH_ANCILLARY_CUSTOMIZE:
          return CUSTOMIZE_STEP;
        case PATH_SHOP:
        default:
          return hotelShopStep;
      }
    })();

    return (
      <>
        {progresses.map((progress: HotelShopStepV2, index: number) => {
          return (
            <React.Fragment key={index}>
              <HotelShopProgressButton
                key={`button-${index}`}
                hasSelectedHotelProduct={hasSelectedHotelProduct}
                selectedStep={selectedStep}
                progress={progress}
                isAddOnOptionAvailable={isAddOnOptionAvailable}
                isOptionSelectionComplete={isOptionSelectionComplete}
                setPremierCollectionShopProgress={
                  setPremierCollectionShopProgress
                }
                goToAvailability={() => {
                  goToAvailability({
                    history,
                    lodging: selectedLodging,
                    fromDate,
                    untilDate,
                    adultsCount,
                    childrenCount,
                  });
                }}
                goToCustomize={() => {
                  goToAncillaryCustomize({ history });
                }}
                goToHotelShopStep={() => {
                  goToShop({ history });
                }}
              />
              {index < progresses.length - 1 && (
                <FontAwesomeIcon
                  key={`separator-${index}`}
                  className="progress-button-separator"
                  icon={faChevronRight}
                />
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const getProgresses = (): HotelShopStepV2[] => {
    const progresses: HotelShopStepV2[] = [];

    progresses.push(AVAILABILITY_STEP);

    // note: PremierCollectionShopStep.HotelInfo and PremierCollectionShopStep.ChooseRoom share the same page
    progresses.push(PremierCollectionShopStep.HotelInfo);

    if (useCustomizeStep) {
      progresses.push(CUSTOMIZE_STEP);
    }

    progresses.push(BOOK_STEP);

    return progresses;
  };

  return (
    <Box className="premier-collection-shop-progress-bar-root">
      <Box className="premier-collection-shop-progress-bar-container">
        <ProgressButtons progresses={getProgresses()} />
      </Box>
    </Box>
  );
};

interface IHotelShopProgressButtonProps {
  // isAddOnOptionAvailable: boolean;
  // isOptionSelectionComplete: boolean;
  hasSelectedHotelProduct: boolean;
  selectedStep: HotelShopStepV2;
  progress: HotelShopStepV2;
  isAddOnOptionAvailable: boolean;
  isOptionSelectionComplete: boolean;
  setPremierCollectionShopProgress: (
    progress: PremierCollectionShopStep
  ) => void;
  goToHotelShopStep?: (progress: PremierCollectionShopStep) => void;
  goToAvailability?: () => void;
  goToCustomize?: () => void;
  goToCheckout?: () => void;
}

const HotelShopProgressButton = (props: IHotelShopProgressButtonProps) => {
  const {
    // isAddOnOptionAvailable,
    // isOptionSelectionComplete,
    hasSelectedHotelProduct,
    selectedStep,
    progress,
    isAddOnOptionAvailable,
    isOptionSelectionComplete,
    setPremierCollectionShopProgress,
    goToHotelShopStep,
    goToAvailability,
    goToCustomize,
    goToCheckout,
  } = props;

  const getProgressText = (progress: HotelShopStepV2) => {
    switch (progress) {
      case AVAILABILITY_STEP: {
        return constants.AVAILABILITY_TEXT;
      }
      case PremierCollectionShopStep.HotelInfo:
      case PremierCollectionShopStep.ChooseRoom: {
        return constants.SHOP_TEXT;
      }
      case CUSTOMIZE_STEP: {
        return constants.CUSTOMIZE_TEXT;
      }
      case BOOK_STEP: {
        return constants.BOOK_TEXT;
      }
      default: {
        return "";
      }
    }
  };

  const compareSteps = (
    step1: HotelShopStepV2,
    step2: HotelShopStepV2
  ): CompareStepsResult => {
    // Only steps in the normal shop flow are added.
    const stepOrdering: HotelShopStepV2[] = [
      AVAILABILITY_STEP,
      PremierCollectionShopStep.HotelInfo,
      PremierCollectionShopStep.ChooseRoom,
      CUSTOMIZE_STEP,
      BOOK_STEP,
    ];

    const index1 = stepOrdering.findIndex((s) => s === step1);
    const index2 = stepOrdering.findIndex((s) => s === step2);

    if (index1 === -1 || index2 === -1) {
      return CompareStepsResult.Unknown;
    } else if (index1 === index2) {
      return CompareStepsResult.Same;
    } else if (index1 < index2) {
      return CompareStepsResult.Before;
    } else {
      return CompareStepsResult.After;
    }
  };

  const isDisabled = (
    progress: HotelShopStepV2,
    selectedStep: HotelShopStepV2
  ) => {
    if (compareSteps(selectedStep, progress) === CompareStepsResult.Before) {
      return true;
    }

    switch (progress) {
      case AVAILABILITY_STEP:
      case PremierCollectionShopStep.HotelInfo:
      case PremierCollectionShopStep.ChooseRoom: {
        return false;
      }
      case CUSTOMIZE_STEP: {
        return !hasSelectedHotelProduct || !isAddOnOptionAvailable;
      }
      case BOOK_STEP: {
        return (
          !hasSelectedHotelProduct ||
          (isAddOnOptionAvailable && !isOptionSelectionComplete)
        );
      }
      default: {
        return true;
      }
    }
  };

  const handleOnClick = () => {
    // note: it prevents the breadscrumb for the selected step from being clicked
    if (compareSteps(selectedStep, progress) === CompareStepsResult.Same) {
      return;
    }

    switch (progress) {
      case AVAILABILITY_STEP: {
        if (goToAvailability) goToAvailability();
        break;
      }
      case CUSTOMIZE_STEP: {
        if (goToCustomize) goToCustomize();
        break;
      }
      /*
        note: given the `isDisabled` logic, the final step (book) is never going to be enabled unless the user
        is on the checkout page; therefore, there is no point in passing in goToCheckout since it wouldn't get called
      */
      case BOOK_STEP: {
        if (goToCheckout) goToCheckout();
        break;
      }
      case PremierCollectionShopStep.HotelInfo:
      case PremierCollectionShopStep.ChooseRoom:
      default: {
        setPremierCollectionShopProgress(progress);
        if (goToHotelShopStep) goToHotelShopStep(progress);
        break;
      }
    }
  };

  return (
    <ActionLink
      className={clsx(
        "hotel-shop-progress-button",
        { selected: selectedStep === progress },
        "b2b"
      )}
      onClick={handleOnClick}
      content={getProgressText(progress)}
      disabled={isDisabled(progress, selectedStep)}
    />
  );
};
